/**
 * Runtime description of `PlanFeatures` (api/src/domain/plan/Plan.ts).
 *
 * The TypeScript interface is erased at build time, so the override form needs
 * its own copy of the shape. Kept deliberately close to the source: when a key
 * is added there, add it here or it stays editable only through raw JSON.
 *
 * The three-way `kind` matters more than it looks. For a capped key, **absent,
 * `null` and a number all mean different things** — absent falls back to a
 * server-side default, `null` means unlimited, a number is the cap. A plain
 * number input cannot express that, which is why `nullableNumber` keys get a
 * mode selector rather than a text field.
 */
export type FeatureKind = 'boolean' | 'nullableNumber' | 'number';

export interface FeatureDescriptor {
  key: string;
  kind: FeatureKind;
  label: string;
  /** Shown under the control. Says what absent falls back to where that is not obvious. */
  help?: string;
}

export interface FeatureGroup {
  title: string;
  /** Collapsed by default — kept editable, but out of the way. */
  legacy?: boolean;
  features: FeatureDescriptor[];
}

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    title: 'Capabilities',
    features: [
      { key: 'customSignals', kind: 'boolean', label: 'Custom signal categories' },
      { key: 'customPersonas', kind: 'boolean', label: 'Custom personas (contact labels)' },
      {
        key: 'socialListening',
        kind: 'boolean',
        label: 'Social listening',
        help: 'Per-stakeholder monitoring. Sits on top of MAX_MONITORED_CONTACTS_PER_PLAN.',
      },
      { key: 'customSalesMethodology', kind: 'boolean', label: 'Custom sales methodology' },
      { key: 'sso', kind: 'boolean', label: 'SSO' },
    ],
  },
  {
    title: 'Integrations',
    features: [
      { key: 'calendarIntegration', kind: 'boolean', label: 'Calendar' },
      { key: 'chatIntegration', kind: 'boolean', label: 'Chat' },
      { key: 'crmIntegration', kind: 'boolean', label: 'CRM' },
      { key: 'emailIntegration', kind: 'boolean', label: 'Email' },
      { key: 'transcriptIntegration', kind: 'boolean', label: 'Transcripts' },
    ],
  },
  {
    title: 'Limits',
    features: [
      { key: 'maxUsers', kind: 'nullableNumber', label: 'Max users' },
      { key: 'maxAccountPlans', kind: 'nullableNumber', label: 'Max account plans' },
      {
        key: 'maxOpportunitiesPerAccountPlan',
        kind: 'nullableNumber',
        label: 'Max opportunities per plan',
        help: 'Absent is not unlimited — it falls back to the default of 8. Use Unlimited to uncap.',
      },
      {
        key: 'maxCompetitorMonitors',
        kind: 'nullableNumber',
        label: 'Max competitor monitors',
        help: 'Counted per distinct competitor company, not per offering link. Absent falls back to the default.',
      },
      {
        key: 'minAccountPlanCommitment',
        kind: 'number',
        label: 'Min account-plan commitment',
        help: 'The floor a commitment may not go below. Policy, not price.',
      },
      { key: 'seats', kind: 'number', label: 'Seats' },
      { key: 'numberOfDeepDiveViews', kind: 'nullableNumber', label: 'Deep-dive views' },
      { key: 'numberOfIntelligenceRequests', kind: 'nullableNumber', label: 'Intelligence requests' },
    ],
  },
  {
    title: 'Legacy',
    legacy: true,
    features: [
      { key: 'askASeller', kind: 'boolean', label: 'Ask a seller' },
      { key: 'assistedOutreach', kind: 'boolean', label: 'Assisted outreach' },
      { key: 'databaseAccess', kind: 'boolean', label: 'Database access' },
      { key: 'enhancedCompanyData', kind: 'boolean', label: 'Enhanced company data' },
      { key: 'includes1x1Calls', kind: 'boolean', label: 'Includes 1:1 calls' },
      { key: 'inclusionInPaidPromotion', kind: 'boolean', label: 'Inclusion in paid promotion' },
      { key: 'premiumMarketplacePlacement', kind: 'boolean', label: 'Premium marketplace placement' },
      { key: 'exclusivityDays', kind: 'number', label: 'Exclusivity days' },
    ],
  },
];

/** Every key the form knows how to render. */
export const KNOWN_FEATURE_KEYS: ReadonlySet<string> = new Set(
  FEATURE_GROUPS.flatMap((group) => group.features.map((feature) => feature.key)),
);

/** How one key is currently set. `default` means the key is absent entirely. */
export type FeatureMode = 'default' | 'off' | 'on' | 'unlimited' | 'value';

/**
 * Which mode a stored value represents.
 *
 * `undefined` is the key being absent — inherit the plan default. That is not
 * the same as `null`, which is an explicit "no limit", so the two must not be
 * collapsed here.
 * @param kind - The descriptor's kind.
 * @param value - The stored override value, or undefined when absent.
 */
export function modeForValue(kind: FeatureKind, value: unknown): FeatureMode {
  if (value === undefined) return 'default';
  if (kind === 'boolean') return value ? 'on' : 'off';
  if (value === null) return 'unlimited';
  return 'value';
}

/**
 * Build the override object the API stores, dropping every key left on
 * "default" so the plan's own value keeps showing through.
 * @param modes - Mode chosen per key.
 * @param numbers - Raw number-field text per key.
 */
export function buildOverrides(
  modes: Record<string, FeatureMode>,
  numbers: Record<string, string>,
): Record<string, unknown> {
  const overrides: Record<string, unknown> = {};

  for (const group of FEATURE_GROUPS) {
    for (const feature of group.features) {
      const mode = modes[feature.key] ?? 'default';
      if (mode === 'default') continue;
      if (mode === 'on') overrides[feature.key] = true;
      else if (mode === 'off') overrides[feature.key] = false;
      else if (mode === 'unlimited') overrides[feature.key] = null;
      else {
        const raw = (numbers[feature.key] ?? '').trim();
        // An empty or unparseable box is the same as not overriding — better
        // than writing NaN into a limit the server enforces.
        if (raw === '') continue;
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) overrides[feature.key] = parsed;
      }
    }
  }

  return overrides;
}
